/**
 * @voltxampere/math — Dense vector primitives
 *
 * Every operation here is immutable — inputs are never mutated and
 * the return value is a freshly-allocated `Float64Array`. Sprint 5's
 * DC solver leans on this contract: an MNA right-hand-side is
 * recomputed each Newton iteration and we cannot afford a stale
 * vector aliasing the previous iterate.
 *
 * `number[]` is forbidden in this package per Sprint 4 discipline
 * (CLAUDE.md). All inputs/outputs are `Float64Array`.
 *
 * `noUncheckedIndexedAccess` makes typed-array access return
 * `number | undefined`. Inside `for (let i = 0; i < a.length; i++)`
 * the index is provably in-bounds, but the type system can't see
 * that — so we use `?? 0` as a no-op fallback. For a `Float64Array`
 * an in-bounds index always returns a number, so the fallback is
 * never observed at runtime.
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
import { ContractViolationError } from "@voltxampere/shared";
/**
 * Allocate a new vector of `size`, optionally pre-filled. Throws if
 * `size` is negative or non-integer; returns the empty array for
 * `size === 0`.
 */
export function createVector(size, fill = 0) {
    if (!Number.isInteger(size) || size < 0) {
        throw new ContractViolationError(`createVector: size must be a non-negative integer, got ${String(size)}`);
    }
    if (!Number.isFinite(fill)) {
        throw new ContractViolationError(`createVector: fill must be finite, got ${String(fill)}`);
    }
    const v = new Float64Array(size);
    if (fill !== 0)
        v.fill(fill);
    return v;
}
function assertSameLength(a, b, op) {
    if (a.length !== b.length) {
        throw new ContractViolationError(`${op}: vector length mismatch (${String(a.length)} vs ${String(b.length)})`);
    }
}
function assertAllFinite(a, op) {
    for (const [i, x] of a.entries()) {
        if (!Number.isFinite(x)) {
            throw new ContractViolationError(`${op}: non-finite value at index ${String(i)} (${String(x)})`);
        }
    }
}
/** Element-wise sum. */
export function vectorAdd(a, b) {
    assertSameLength(a, b, "vectorAdd");
    const out = new Float64Array(a.length);
    for (let i = 0; i < a.length; i++)
        out[i] = (a[i] ?? 0) + (b[i] ?? 0);
    return out;
}
/** Element-wise difference `a - b`. */
export function vectorSub(a, b) {
    assertSameLength(a, b, "vectorSub");
    const out = new Float64Array(a.length);
    for (let i = 0; i < a.length; i++)
        out[i] = (a[i] ?? 0) - (b[i] ?? 0);
    return out;
}
/** Scale every element by `s`. */
export function vectorScale(a, s) {
    if (!Number.isFinite(s)) {
        throw new ContractViolationError(`vectorScale: scalar must be finite, got ${String(s)}`);
    }
    const out = new Float64Array(a.length);
    for (let i = 0; i < a.length; i++)
        out[i] = (a[i] ?? 0) * s;
    return out;
}
/** Inner / dot product. */
export function vectorDot(a, b) {
    assertSameLength(a, b, "vectorDot");
    let acc = 0;
    for (let i = 0; i < a.length; i++)
        acc += (a[i] ?? 0) * (b[i] ?? 0);
    return acc;
}
/** Euclidean (L2) norm. */
export function vectorNorm(a) {
    let acc = 0;
    for (const x of a)
        acc += x * x;
    return Math.sqrt(acc);
}
/** Infinity norm — `max |a_i|`. Empty vector returns 0. */
export function vectorNormInf(a) {
    let m = 0;
    for (const v of a) {
        const x = Math.abs(v);
        if (x > m)
            m = x;
    }
    return m;
}
/**
 * Element-wise approximate equality. `tol` is the absolute tolerance
 * applied per component. For SPICE-style mixed reltol/abstol checks
 * use `equalsWithTol` from the tolerance module.
 */
export function vectorEquals(a, b, tol = 0) {
    if (a.length !== b.length)
        return false;
    if (!Number.isFinite(tol) || tol < 0) {
        throw new ContractViolationError(`vectorEquals: tol must be a non-negative finite number, got ${String(tol)}`);
    }
    for (const [i, av] of a.entries()) {
        if (Math.abs(av - (b[i] ?? 0)) > tol)
            return false;
    }
    return true;
}
/** Convenience guard re-export — useful in tests/diagnostics. */
export function isFiniteVector(a) {
    for (const v of a) {
        if (!Number.isFinite(v))
            return false;
    }
    return true;
}
/** Internal helper — exported so other math modules can validate inputs uniformly. */
export function assertVectorFinite(a, op) {
    assertAllFinite(a, op);
}
//# sourceMappingURL=vector.js.map